import React, { useState, useMemo, useEffect } from 'react';
import type { ClientStepperProps, ClientStepperData, ClientStepType, PlanDetailsData } from '../../types/customer-portal';
import { usePlanSelection } from '../../contexts/PlanSelectionContext';
import ApplicantDetailsStep from './steps/ApplicantDetailsStep';
import PrincipalInsuredStep from './steps/PrincipalInsuredStep';
import HomeAddressStep from './steps/HomeAddressStep';
import ContactInformationStep from './steps/ContactInformationStep';
import BeneficiaryDetailsStep from './steps/BeneficiaryDetailsStep';
import FamilyInformationStep from './steps/FamilyInformationStep';
import PersonalAccidentBeneficiaryStep from './steps/PersonalAccidentBeneficiaryStep';
import GroupMembersInformationStep from './steps/GroupMembersInformationStep';
import SummaryStep from './steps/SummaryStep';
import './ClientInformationStepper.css';
import { STORAGE_KEYS } from '../../utils/sessionStateManager';
import { resolveUtmTrackingData } from '@/shared/utils/utmUtils';

const ClientInformationStepper: React.FC<ClientStepperProps> = ({
  coverageType,
  planType,
  planSelection,
  travelDetailsData,
  onBack,
  onComplete
}) => {
  const planSelectionTempDataLocalStorageState = localStorage.getItem('planSelectionTempData');
  const customerPortalSessionState = sessionStorage.getItem(STORAGE_KEYS.SESSION_STATE);
  const customerPortalLocalStorageState = localStorage.getItem(STORAGE_KEYS.SESSION_STATE);

  // Access premium adjustments and tempData from context
  const { premiumAdjustments, getTempDataValue } = usePlanSelection();
  
  // Get the actual plan type from props first, then fallback to tempData context
  const actualPlanType = useMemo(() => {

    if(customerPortalSessionState){
        const parsedCustomerPortalSessionState = JSON.parse(customerPortalSessionState);
        const planTypeToCheck = parsedCustomerPortalSessionState?.travelFormData?.planType || 'Individual';
        return planTypeToCheck;
    }

    console.log('ClientInformationStepper: Resolving actualPlanType', {
      propPlanType: planType,
      tempDataPlanType: getTempDataValue('planType'),
      tempDataTypeName: getTempDataValue('typeName'),
      tempDataResolvedPlanType: getTempDataValue('resolvedPlanType')
    });

    // Priority 1: Use the prop planType if it's valid and not undefined/empty
    if (planType && planType.trim() !== '') {
      console.log('ClientInformationStepper: Using prop planType:', planType);
      return planType;
    }
    
    // Priority 2: Use tempData resolvedPlanType if available (from EmailVerifiedSection)
    const tempDataResolvedPlanType = getTempDataValue('resolvedPlanType') as string;
    if (tempDataResolvedPlanType && tempDataResolvedPlanType.trim() !== '') {
      console.log('ClientInformationStepper: Using tempData resolvedPlanType:', tempDataResolvedPlanType);
      return tempDataResolvedPlanType;
    }
    
    // Priority 3: Use tempData typeName if available
    const tempDataTypeName = getTempDataValue('typeName') as string;
    if (tempDataTypeName && tempDataTypeName.trim() !== '') {
      console.log('ClientInformationStepper: Using tempData typeName:', tempDataTypeName);
      return tempDataTypeName;
    }
    
    // Priority 4: Map planType number to string if available
    const tempDataPlanType = getTempDataValue('planType');
    if (tempDataPlanType !== undefined && tempDataPlanType !== null) {
      const planTypeNumber = typeof tempDataPlanType === 'number' ? tempDataPlanType : parseInt(String(tempDataPlanType), 10);
      if (!isNaN(planTypeNumber)) {
        let mappedPlanType: string;
        switch (planTypeNumber) {
          case 1: mappedPlanType = 'Individual'; break;
          case 2: mappedPlanType = 'Family'; break;
          case 3: mappedPlanType = 'Group'; break;
          case 4: mappedPlanType = 'Multi Trip'; break;
          default: mappedPlanType = 'Individual'; break;
        }
        console.log('ClientInformationStepper: Using mapped planType from number:', planTypeNumber, '->', mappedPlanType);
        return mappedPlanType;
      }
    }
    
    // Final fallback
    console.log('ClientInformationStepper: Using final fallback planType: Individual');
    return 'Individual';
  }, [getTempDataValue, planType]);
  
  // Set initial step based on plan type and coverage type
  const getInitialStep = (): ClientStepType => {
    if (actualPlanType === 'Group') {
      return 'group-members-information';
    } else if (coverageType === 'someone-else' && actualPlanType !== 'Multi Trip') {
      return 'applicant-details';
    } else {
      return 'principal-insured';
    }
  };
  
  // Define steps based on coverage type and plan type - MOVED UP for proper dependency management
  const steps: { key: ClientStepType; title: string; subtitle?: string; number: number }[] = useMemo(() => {
    if (actualPlanType === 'Group') {
      // Group Plan: 2 steps (removed home-address - addresses collected in GroupMemberModal)
      return [
        { key: 'group-members-information', title: 'Member Details', number: 1 },
        { key: 'summary', title: 'Summary', number: 2 }
      ];
    } else if (actualPlanType === 'Family') {
      // Family Plan: 7 steps (home-address restored)
      return [
        { key: 'principal-insured', title: 'Principal Insured', number: 1 },
        { key: 'home-address', title: 'Home Address', number: 2 },
        { key: 'contact-information', title: 'Contact Information', number: 3 },
        { key: 'beneficiary-details', title: 'Beneficiary Details', number: 4 },
        { key: 'family-information', title: 'Family Information', number: 5 },
        { key: 'personal-accident-beneficiary', title: 'Personal Accident Beneficiary', number: 6 },
        { key: 'summary', title: 'Summary', number: 7 }
      ];
    } else if (actualPlanType === 'Multi Trip' || coverageType === 'myself') {
      // Multi-Trip Plan or Individual Plan - Myself: 5 steps (home-address restored)
      return [
        { key: 'principal-insured', title: 'Principal Insured', number: 1 },
        { key: 'home-address', title: 'Home Address', number: 2 },
        { key: 'contact-information', title: 'Contact Information', number: 3 },
        { key: 'beneficiary-details', title: 'Beneficiary Details', number: 4 },
        { key: 'summary', title: 'Summary', number: 5 }
      ];
    } else {
      // Individual Plan - Someone Else: 6 steps (home-address restored)
      return [
        { key: 'applicant-details', title: 'Applicant Details', number: 1 },
        { key: 'principal-insured', title: 'Principal Insured', number: 2 },
        { key: 'home-address', title: 'Home Address', number: 3 },
        { key: 'contact-information', title: 'Contact Information', number: 4 },
        { key: 'beneficiary-details', title: 'Beneficiary Details', number: 5 },
        { key: 'summary', title: 'Summary', number: 6 }
      ];
    }
  }, [actualPlanType, coverageType]);

  // CRITICAL FIX: Initialize stepper state with sessionStorage (clears when tab closes)
  const [currentStep, setCurrentStep] = useState<ClientStepType>(() => {
    // Try to restore from sessionStorage first - clears when tab closes
    const saved = sessionStorage.getItem('clientInformationStepperState');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('ClientInformationStepper: Restored currentStep from sessionStorage:', parsed.currentStep);
        return parsed.currentStep || getInitialStep();
      } catch (error) {
        console.error('ClientInformationStepper: Error parsing saved stepper state:', error);
      }
    }
    return getInitialStep();
  });
  
  const [stepperData, setStepperData] = useState<ClientStepperData>(() => {
    // Try to restore from sessionStorage first - clears when tab closes
    const saved = sessionStorage.getItem('clientInformationStepperState');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('ClientInformationStepper: Restored stepperData from sessionStorage:', parsed.stepperData);
        return parsed.stepperData || {};
      } catch (error) {
        console.error('ClientInformationStepper: Error parsing saved stepper data:', error);
      }
    }
    return {};
  });
  
  // DEBUG: Log when component mounts
  useEffect(() => {
    console.log('ClientInformationStepper: 🚀 COMPONENT MOUNTED with props:', {
      coverageType,
      planType,
      actualPlanType,
      initialStep: getInitialStep(),
      currentStep,
      stepsLength: steps.length,
      stepsKeys: steps.map(s => s.key)
    });
  }, [actualPlanType, coverageType, steps]);

  
  const parseTempDataFromLocalStoraToObj = (actualPlanType: string) => {
    let travelFormData = {
      tripType: "",
      planType: "",
      startTrip: "",
      destination: [],
      endTrip: "",
      purposeOfTravel: "",
      startDate: "",
      endDate: "",
      travelDate: "",
      tripCategory: "",
      homeCountry: "",
    };
    if (planSelectionTempDataLocalStorageState) {
      const parsedPlanSelectionTempState = JSON.parse(
        planSelectionTempDataLocalStorageState
      );

      if (!parsedPlanSelectionTempState.travelFormData) {
        const isMultiTrip =
          parsedPlanSelectionTempState?.typeName === "Multi Trip";
        const lowerStr = (
          parsedPlanSelectionTempState?.productName || ""
        ).toLowerCase();

        // Keywords to search for
        const keywords = ["worldwide", "elit", "asia", "oceania"];

        // Check each keyword
        const isInternational = keywords.filter((keyword) =>
          lowerStr.includes(keyword)
        );
        travelFormData = {
          tripType: isMultiTrip
            ? "Annual Multi-Trip"
            : parsedPlanSelectionTempState?.typeName,
          planType: parsedPlanSelectionTempState?.resolvedPlanType,
          startTrip: isMultiTrip
            ? ""
            : parsedPlanSelectionTempState?.startTrip || "",
          destination: isMultiTrip
            ? []
            : parsedPlanSelectionTempState?.destination || [],
          endTrip: isMultiTrip
            ? ""
            : parsedPlanSelectionTempState?.endTrip || "",
          purposeOfTravel: parsedPlanSelectionTempState?.purposeOfTravel,
          startDate: isMultiTrip ? "" : parsedPlanSelectionTempState?.startDate,
          endDate: isMultiTrip ? "" : parsedPlanSelectionTempState?.startDate,
          travelDate: isMultiTrip
            ? parsedPlanSelectionTempState?.startDate
            : "",
          tripCategory: isInternational ? "International" : "Domestic",
          homeCountry: "",
        };
        const updatedState = {
          planSelection: {
            planType: actualPlanType,
          },
          plan: {
            planType: `${
              parsedPlanSelectionTempState.currencyCode === "USD"
                ? "Dollar"
                : "Peso"
            } Plan`,
            productId: parsedPlanSelectionTempState.productId,
            productLabel: parsedPlanSelectionTempState.productLabel,
            productName: parsedPlanSelectionTempState.productName,
          },
          travelFormData,
        };

        sessionStorage.setItem(STORAGE_KEYS.SESSION_STATE, JSON.stringify(updatedState));
      }
    }
  };
  
  // CRITICAL FIX: Save stepper state to sessionStorage (clears when tab closes)
  useEffect(() => {
    if (currentStep && actualPlanType && coverageType) {

      if(!customerPortalSessionState) {
        if (customerPortalLocalStorageState) {
        sessionStorage.setItem(STORAGE_KEYS.SESSION_STATE, customerPortalLocalStorageState);
        }  else {
            parseTempDataFromLocalStoraToObj(actualPlanType);
        }
      }
      

      const stepperState = {
        currentStep,
        stepperData,
        planType: actualPlanType,
        coverageType,
        timestamp: new Date().toISOString()
      };
      
      sessionStorage.setItem('clientInformationStepperState', JSON.stringify(stepperState));
      console.log('ClientInformationStepper: ✅ SESSIONSTORAGE SAVE - stepper state saved:', {
        currentStep,
        planType: actualPlanType,
        coverageType,
        hasStepperData: Object.keys(stepperData).length > 0
      });
    }
  }, [currentStep, stepperData, actualPlanType, coverageType]);

  // CRITICAL FIX: Clear sessionStorage when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if we're actually navigating away, not just refreshing
      const isRefresh = performance.navigation?.type === 1 ||
                       (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';
      
      if (!isRefresh) {
        sessionStorage.removeItem('clientInformationStepperState');
        console.log('ClientInformationStepper: Cleared stepper state from sessionStorage on navigation');
      }
    };
  }, []);



  const currentStepIndex = steps.findIndex(step => step.key === currentStep);
  const currentStepInfo = steps[currentStepIndex];
  const totalSteps = steps.length;

  const utmTrackingData = resolveUtmTrackingData();

  // Safety check to prevent undefined errors
  if (!currentStepInfo) {
    console.error(`Current step "${currentStep}" not found in steps array for plan type "${actualPlanType}"`);
    return <div>Error: Invalid step configuration</div>;
  }

  const handleStepData = (stepKey: ClientStepType, data: unknown) => {
    // Map step keys to the correct data keys as defined in ClientStepperData
    const stepKeyMap: Record<ClientStepType, keyof ClientStepperData> = {
      'applicant-details': 'applicantdetails',
      'principal-insured': 'principalinsured',
      'home-address': 'homeaddress',
      'contact-information': 'contactinformation',
      'beneficiary-details': 'beneficiarydetails',
      'family-information': 'familyinformation',
      'personal-accident-beneficiary': 'personalaccidentbeneficiary',
      'group-members-information': 'groupinformation',
      'summary': 'principalinsured' // Summary doesn't store data, reuse principal
    };

    const dataKey = stepKeyMap[stepKey];
    if (dataKey && stepKey !== 'summary') {
      console.log(`ClientInformationStepper: Storing data for step ${stepKey}:`, data);
      setStepperData(prev => ({
        ...prev,
        [dataKey]: data
      }));
    }
  };

  const handleNext = (data?: unknown) => {
    console.log('ClientInformationStepper: 🔄 handleNext called:', {
      currentStep,
      hasData: !!data,
      currentStepIndex,
      totalSteps: steps.length
    });

    if (data) {
      console.log('ClientInformationStepper: 💾 Storing step data for:', currentStep, data);
      handleStepData(currentStep, data);
    }

    if (currentStepIndex < steps.length - 1) {
      const nextStep = steps[currentStepIndex + 1].key;
      console.log('ClientInformationStepper: ➡️ Moving to next step:', nextStep);
      setCurrentStep(nextStep);
    } else {
      // Final step - complete the process
      const finalData = { ...stepperData };
      if (data) {
        // Use the same mapping logic as handleStepData
        const stepKeyMap: Record<ClientStepType, keyof ClientStepperData> = {
          'applicant-details': 'applicantdetails',
          'principal-insured': 'principalinsured',
          'home-address': 'homeaddress',
          'contact-information': 'contactinformation',
          'beneficiary-details': 'beneficiarydetails',
          'family-information': 'familyinformation',
          'personal-accident-beneficiary': 'personalaccidentbeneficiary',
          'group-members-information': 'groupinformation',
          'summary': 'principalinsured'
        };
        const dataKey = stepKeyMap[currentStep];
        if (dataKey && currentStep !== 'summary') {
          (finalData as Record<string, unknown>)[dataKey] = data;
        }
      }
      // CRITICAL FIX: Clear stepper state when completing
      console.log('ClientInformationStepper: ✅ Completing stepper, clearing stepper state');
      sessionStorage.removeItem('clientInformationStepperState');
      
      onComplete(finalData);
    }
  };

  const handlePrevious = () => {
    console.log("ClientInformationStepper: steps", steps[currentStepIndex - 1].key, steps)
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].key);
    } else {
      onBack();
    }
  };

  const handleEditDetails = () => {
    // Navigate to Step 1 based on plan type and coverage type while preserving all data
    let firstStep: ClientStepType;
    if (actualPlanType === 'Group') {
      firstStep = 'group-members-information';
    } else if (coverageType === 'someone-else' && actualPlanType !== 'Multi Trip') {
      firstStep = 'applicant-details';
    } else {
      firstStep = 'principal-insured';
    }
    setCurrentStep(firstStep);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'applicant-details':
        return (
          <ApplicantDetailsStep
            data={stepperData.applicantdetails}
            onNext={handleNext}
            onBack={handlePrevious}
          />
        );
      case 'principal-insured':
        return (
          <PrincipalInsuredStep
            data={stepperData.principalinsured}
            onNext={handleNext}
            onBack={handlePrevious}
          />
        );
      case 'home-address':
        return (
          <HomeAddressStep
            data={stepperData.homeaddress}
            onNext={handleNext}
            onBack={handlePrevious}
          />
        );
      case 'contact-information':
        return (
          <ContactInformationStep
            data={stepperData.contactinformation}
            onNext={handleNext}
            onBack={handlePrevious}
          />
        );
      case 'beneficiary-details':
        return (
          <BeneficiaryDetailsStep
            data={stepperData.beneficiarydetails}
            onNext={handleNext}
            onBack={handlePrevious}
          />
        );
      case 'family-information':
        return (
          <FamilyInformationStep
            data={stepperData.familyinformation}
            onNext={handleNext}
            onBack={handlePrevious}
            principalHomeAddress={stepperData.homeaddress}
          />
        );
      case 'personal-accident-beneficiary':
        return (
          <PersonalAccidentBeneficiaryStep
            data={stepperData.personalaccidentbeneficiary}
            beneficiaryData={stepperData.beneficiarydetails}
            onNext={handleNext}
            onBack={handlePrevious}
          />
        );
      case 'group-members-information':
        return (
          <GroupMembersInformationStep
            data={stepperData.groupinformation}
            onNext={handleNext}
            onBack={handlePrevious}
          />
        );
      case 'summary': {
        // Mock plan data for now - this will be replaced with real data from plan selection
        const mockPlanData: PlanDetailsData = {
          planName: 'Domestic-Bronze Individual',
          productType: 'Travelsafe',
          typeOfPlan: 'Individual',
          plan: 'Domestic-Bronze Individual',
          medicalTreatmentLimit: 'PHP 500,000',
          originalPremium: 'PHP 532',
          paymentPremium: 'PHP 532'
        };

        return (
          <SummaryStep
            data={stepperData}
            planData={mockPlanData}
            travelData={undefined}
            planSelection={planSelection}
            travelDetailsData={travelDetailsData}
            premiumAdjustments={premiumAdjustments}
            onNext={handleNext}
            onBack={handlePrevious}
            onEditDetails={handleEditDetails}
            utmData={utmTrackingData}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="client-stepper-container">
      <div
        className="client-stepper-background"
      />
      <div className="client-stepper-overlay">
        <div className="client-stepper-header">
          <h1 className="client-stepper-title">
            {currentStep === 'family-information'
              ? 'Family Information'
              : currentStep === 'personal-accident-beneficiary'
              ? 'Personal Accident Beneficiary'
              : currentStep === 'summary'
              ? 'Summary'
              : 'Client Information'}
          </h1>
          <p className="client-stepper-subtitle">
            {currentStep === 'family-information'
              ? 'The family member/s who will join you in the trip'
              : currentStep === 'personal-accident-beneficiary'
              ? 'The policy benefits will be paid to the nominated beneficiary/ies.'
              : currentStep === 'group-members-information'
              ? 'Please provide details.'
              : currentStep === 'summary'
              ? 'Here\'s a quick summary of your plan, profile, and travel info. Feel free to update anything if needed.'
              : 'Please provide details.'}
          </p>
        </div>
      </div>

      <div className="client-stepper-content">
        {/* Stepper Card */}
        <div className="client-stepper-card">
          {/* Progress Header */}
          <div className="stepper-progress-header">
            <div className="stepper-plan-info">
              <h2 className="stepper-plan-title">
                {actualPlanType === 'Family' ? 'Family Plan' :
                 actualPlanType === 'Group' ? 'Group Plan' :
                 actualPlanType === 'Multi Trip' ? 'Multi-Trip Plan' : 'Individual Plan'}
              </h2>
            </div>
            <div className="stepper-step-info">
              <span className="stepper-step-text">Step {currentStepInfo?.number || 1} of {totalSteps}</span>
              <span className="stepper-step-title">{currentStepInfo?.title || 'Unknown Step'}</span>
              {currentStepInfo?.subtitle && (
                <span className="stepper-step-subtitle">{currentStepInfo.subtitle}</span>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="stepper-progress-bar">
            <div 
              className="stepper-progress-fill"
              style={{ width: `${(currentStepInfo.number / totalSteps) * 100}%` }}
            />
          </div>

          {/* Step Content */}
          <div className="stepper-step-content">
            {renderCurrentStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientInformationStepper;